import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { Icons } from '../utils/icons';
import api from '../utils/api';
import { fontStack, glassCard, dInput, btnPrimary, btnSmall, btnDanger, btnGhost } from '../styles/shared';
import ConfirmModal from './ConfirmModal';
import useConfirm from '../hooks/useConfirm';
import CustomSelect from './CustomSelect';

const INTERVALS = [
    { key: 'weekly',   label: 'Weekly',    days: 7,   period: 'week' },
    { key: 'monthly',  label: 'Monthly',   days: 30,  period: 'month' },
    { key: '3months',  label: '3 Months',  days: 90,  period: '3 months' },
    { key: '1year',    label: '1 Year',    days: 365, period: 'year' },
];

const STATUS_COLORS = {
    active:   { bg: 'var(--success-bg)', color: 'var(--success)', border: 'var(--success-border)' },
    trialing: { bg: 'rgba(99,102,241,0.1)', color: '#818cf8', border: 'rgba(99,102,241,0.3)' },
    pending:  { bg: 'var(--warning-bg, rgba(255,165,0,0.1))', color: 'var(--warning, #ffa500)', border: 'var(--warning-border, rgba(255,165,0,0.3))' },
    past_due: { bg: 'var(--warning-bg, rgba(255,165,0,0.1))', color: 'var(--warning, #ffa500)', border: 'var(--warning-border, rgba(255,165,0,0.3))' },
    canceled: { bg: 'var(--error-bg)', color: 'var(--error)', border: 'var(--error-border)' },
    expired:  { bg: 'var(--error-bg)', color: 'var(--error)', border: 'var(--error-border)' },
};

const card = {
    ...glassCard,
    padding: '24px',
    marginBottom: '20px',
};

const label = {
    fontSize: '13px',
    fontWeight: 600,
    color: 'var(--text-muted)',
    marginBottom: '6px',
    display: 'block',
};

export default function SubscriptionsTab({ channel }) {
    const { token, user } = useAuth();
    const isDemoUser = user?.username === 'hdurumodel3';
    const toast = useToast();
    const { confirm, confirmProps } = useConfirm();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [config, setConfig] = useState(null);
    const [vipJoinByRequest, setVipJoinByRequest] = useState(null);
    const [plans, setPlans] = useState(
        INTERVALS.map(i => ({ interval_key: i.key, price_usd: '', enabled: false, whop_plan_id: '', original_price_usd: '', initial_price_usd: '', promo_code: '', show_discount_code: true, trial_period_days: 0 }))
    );
    const [vipChatId, setVipChatId] = useState('');
    const [vipChatTitle, setVipChatTitle] = useState('');
    const [enabled, setEnabled] = useState(true);
    const [promoText, setPromoText] = useState('');
    const [ctaLabel, setCtaLabel] = useState('Subscribe Now');
    const [promoBanner, setPromoBanner] = useState('');
    const [uploadingBanner, setUploadingBanner] = useState(false);
    const [posting, setPosting] = useState(false);
    const [autoPostEnabled, setAutoPostEnabled] = useState(false);
    const [autoPostInterval, setAutoPostInterval] = useState(24);
    const [autoPostUnit, setAutoPostUnit] = useState('hours');
    const bannerInputRef = useRef(null);

    // Subscribers
    const [subsTab, setSubsTab] = useState('active');
    const [subscribers, setSubscribers] = useState([]);
    const [subsTotal, setSubsTotal] = useState(0);
    const [subsPage, setSubsPage] = useState(1);
    const [subsLoading, setSubsLoading] = useState(false);
    const [expandedSub, setExpandedSub] = useState(null);
    const [subDetails, setSubDetails] = useState({});
    const [subDetailsLoading, setSubDetailsLoading] = useState({});

    // Analytics
    const [analytics, setAnalytics] = useState(null);
    const [analyticsLoading, setAnalyticsLoading] = useState(false);

    // Expanded discount sections per plan
    const [expandedPlans, setExpandedPlans] = useState({});
    const [chartHover, setChartHover] = useState(null); // index into daily_checkouts
    const [showGuide, setShowGuide] = useState(false);
    const [channelSetupOpen, setChannelSetupOpen] = useState(null);
    const [promoSettingsOpen, setPromoSettingsOpen] = useState(false);

    // VIP channel connect flow
    const [connectingVip, setConnectingVip] = useState(false);

    // Track saved state for unsaved changes detection
    const savedConfigRef = useRef(null);

    useEffect(() => {
        loadConfig();
        loadSubscribers(1, 'active');
        loadAnalytics();
    }, [channel]);

    // Poll for VIP channel detection while connecting
    useEffect(() => {
        if (!connectingVip) return;
        const interval = setInterval(async () => {
            try {
                const res = await api.post('/api/subscriptions/config/get', { token, channel_username: channel });
                const cfg = res.data.config;
                if (cfg && cfg.vip_chat_id) {
                    setConfig(cfg);
                    setVipChatId(cfg.vip_chat_id);
                    setVipChatTitle(cfg.vip_chat_title || '');
                    setConnectingVip(false);
                    toast.success('Subscription channel connected!');
                }
            } catch (err) {
                // ignore polling errors
            }
        }, 2000);
        return () => clearInterval(interval);
    }, [connectingVip, channel, token]);

    const loadConfig = async () => {
        setLoading(true);
        try {
            const res = await api.post('/api/subscriptions/config/get', { token, channel_username: channel });
            const cfg = res.data.config;
            const serverPlans = res.data.plans || [];
            setVipJoinByRequest(res.data.vip_join_by_request);

            if (cfg) {
                setConfig(cfg);
                setVipChatId(cfg.vip_chat_id || '');
                setVipChatTitle(cfg.vip_chat_title || '');
                setEnabled(cfg.enabled !== false);
                setPromoText(cfg.promo_text || '');
                setCtaLabel(cfg.cta_button_label || 'Subscribe Now');
                setPromoBanner(cfg.promo_banner_file_id || '');
                setChannelSetupOpen(!cfg.vip_chat_id);
                setAutoPostEnabled(cfg.auto_post_enabled || false);
                const totalMins = cfg.auto_post_interval_minutes || 1440;
                if (totalMins >= 1440 && totalMins % 1440 === 0) {
                    setAutoPostInterval(totalMins / 1440);
                    setAutoPostUnit('days');
                } else {
                    setAutoPostInterval(Math.round(totalMins / 60));
                    setAutoPostUnit('hours');
                }
            } else {
                setConfig(null);
                setVipChatId('');
                setVipChatTitle('');
                setEnabled(true);
                setChannelSetupOpen(true);
                setPromoText('');
                setCtaLabel('Subscribe Now');
                setPromoBanner('');
            }

            // Merge server plans with interval definitions
            const PLAN_DEFAULTS = {
                monthly: { price_usd: '20', enabled: true, original_price_usd: '30' },
                '1year':  { price_usd: '200', enabled: true, original_price_usd: '360' },
            };
            const mergedPlans = INTERVALS.map(i => {
                const sp = serverPlans.find(p => p.interval_key === i.key);
                const def = PLAN_DEFAULTS[i.key] || {};
                return {
                    interval_key: i.key,
                    price_usd: sp ? String(sp.price_usd) : (def.price_usd || ''),
                    enabled: sp ? sp.enabled : (def.enabled || false),
                    whop_plan_id: sp ? sp.whop_plan_id : '',
                    original_price_usd: sp && sp.original_price_usd != null ? String(sp.original_price_usd) : (def.original_price_usd || ''),
                    initial_price_usd: sp && sp.initial_price_usd != null ? String(sp.initial_price_usd) : '',
                    promo_code: sp ? (sp.promo_code || '') : '',
                    show_discount_code: sp ? (sp.show_discount_code !== false) : true,
                    trial_period_days: sp ? (sp.trial_period_days || 0) : 0,
                };
            });
            setPlans(mergedPlans);

            // Snapshot saved state for dirty detection
            savedConfigRef.current = {
                enabled: cfg ? cfg.enabled !== false : true,
                promoText: cfg?.promo_text || '',
                ctaLabel: cfg?.cta_button_label || 'Subscribe Now',
                promoBanner: cfg?.promo_banner_file_id || '',
                vipChatTitle: cfg?.vip_chat_title || '',
                plans: mergedPlans.map(p => ({ interval_key: p.interval_key, price_usd: p.price_usd, enabled: p.enabled, original_price_usd: p.original_price_usd, initial_price_usd: p.initial_price_usd, promo_code: p.promo_code, show_discount_code: p.show_discount_code, trial_period_days: p.trial_period_days })),
            };
        } catch (err) {
            console.error('Load config error:', err);
        } finally {
            setLoading(false);
        }
    };

    const loadSubscribers = async (page, tab) => {
        const statusFilter = tab || subsTab;
        setSubsLoading(true);
        try {
            const res = await api.get(`/api/subscriptions/subscribers?channel=${encodeURIComponent(channel)}&page=${page}&per_page=20&status=${statusFilter}`);
            setSubscribers(res.data.subscribers || []);
            setSubsTotal(res.data.total || 0);
            setSubsPage(page);
        } catch (err) {
            console.error('Load subscribers error:', err);
        } finally {
            setSubsLoading(false);
        }
    };

    const loadAnalytics = async () => {
        setAnalyticsLoading(true);
        try {
            const res = await api.get(`/api/subscriptions/analytics?channel=${encodeURIComponent(channel)}`);
            if (isDemoUser) {
                // Fake analytics for promo video
                const totalCheckouts = Math.round(1396 / 0.23);
                const fakeDaily = [];
                const now = new Date();
                let prevC = 120;
                let prevV = 28;
                for (let i = 30; i >= 0; i--) {
                    const d = new Date(now); d.setDate(d.getDate() - i);
                    const dayOfWeek = d.getDay();
                    const weekendDip = (dayOfWeek === 0 || dayOfWeek === 6) ? 0.78 : 1.0;
                    const progress = (30 - i) / 30;
                    // Growth curve matching overview: starts ~120, ends ~230
                    const trendC = 110 + progress * progress * 130;
                    const stepC = Math.sin(i * 1.1) * 18 + Math.cos(i * 0.7) * 12 + Math.sin(i * 2.3) * 8;
                    prevC = prevC * 0.4 + (trendC + stepC) * 0.6;
                    const checkouts = Math.max(80, Math.min(260, Math.round(prevC * weekendDip)));
                    // Converted follows checkouts with ~23% rate + slight variation
                    const trendV = checkouts * (0.21 + progress * 0.04);
                    const stepV = Math.sin(i * 0.9) * 4 + Math.cos(i * 1.3) * 3;
                    prevV = prevV * 0.35 + (trendV + stepV) * 0.65;
                    const converted = Math.max(15, Math.min(65, Math.round(prevV * weekendDip)));
                    fakeDaily.push({ date: d.toISOString().slice(0, 10), checkouts, converted });
                }
                setAnalytics({
                    total_checkouts: totalCheckouts,
                    completed: 1396,
                    abandoned: totalCheckouts - 1396,
                    conversion_rate: 23.0,
                    active_subscribers: 593,
                    trialing: 24,
                    past_due: 8,
                    churned: 186,
                    churn_rate: 6.2,
                    mrr: 8450,
                    total_revenue: 23400,
                    plan_breakdown: [
                        { interval_key: 'weekly', active: 47, churned: 32, abandoned: 890 },
                        { interval_key: 'monthly', active: 418, churned: 124, abandoned: 2680 },
                        { interval_key: '3months', active: 89, churned: 22, abandoned: 540 },
                        { interval_key: '1year', active: 39, churned: 8, abandoned: 180 },
                    ],
                    monthly_trend: [],
                    daily_checkouts: fakeDaily,
                });
            } else {
                setAnalytics(res.data);
            }
        } catch (err) {
            console.error('Load analytics error:', err);
        } finally {
            setAnalyticsLoading(false);
        }
    };

    const switchSubsTab = (tab) => {
        setSubsTab(tab);
        setSubsPage(1);
        loadSubscribers(1, tab);
    };

    const toggleSubDetails = async (subId) => {
        if (expandedSub === subId) {
            setExpandedSub(null);
            return;
        }
        setExpandedSub(subId);
        if (subDetails[subId]) return; // already loaded
        setSubDetailsLoading(prev => ({ ...prev, [subId]: true }));
        try {
            const res = await api.get(`/api/subscriptions/subscriber-details?id=${subId}`);
            setSubDetails(prev => ({ ...prev, [subId]: res.data }));
        } catch (err) {
            console.error('Load subscriber details error:', err);
        } finally {
            setSubDetailsLoading(prev => ({ ...prev, [subId]: false }));
        }
    };

    const requestConnectVip = async () => {
        try {
            await api.post('/api/subscriptions/request-add-vip-channel', { token, channel_username: channel });
            setConnectingVip(true);
            toast.success('Check your Telegram for the connect button!');
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to request channel setup');
        }
    };

    const handleBannerUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 10 * 1024 * 1024) { toast.error('Image too large (max 10MB)'); return; }
        setUploadingBanner(true);
        try {
            const fd = new FormData();
            fd.append('token', token);
            fd.append('file', file);
            fd.append('type', 'banner');
            const res = await api.post('/api/dashboard/upload', fd);
            setPromoBanner(res.data.file_id);
            toast.success('Banner uploaded');
        } catch (err) {
            toast.error(err.response?.data?.error || 'Upload failed');
        } finally {
            setUploadingBanner(false);
            e.target.value = '';
        }
    };

    const handlePostPromo = async () => {
        if (!vipChatId) { toast.error('Connect a subscription channel first'); return; }
        setPosting(true);
        try {
            await api.post('/api/subscriptions/post-promo', { token, channel_username: channel });
            toast.success('Promo posted to channel!');
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to post promo');
        } finally {
            setPosting(false);
        }
    };

    const handleSave = async () => {
        if (!vipChatId) {
            toast.error('Please enter a Channel Chat ID');
            return;
        }
        if (autoPostEnabled && (!autoPostInterval || autoPostInterval < 1)) {
            toast.error('Minimum auto-post interval is 1 hour');
            setAutoPostInterval(1);
            return;
        }
        setSaving(true);
        try {
            const res = await api.post('/api/subscriptions/config/save', {
                token,
                channel_username: channel,
                vip_chat_id: Number(vipChatId),
                vip_chat_title: vipChatTitle,
                enabled,
                promo_text: promoText,
                cta_button_label: ctaLabel,
                promo_banner_file_id: promoBanner,
                auto_post_enabled: autoPostEnabled,
                auto_post_interval_minutes: autoPostUnit === 'days' ? autoPostInterval * 1440 : autoPostInterval * 60,
                plans: plans.map(p => {
                    const origVal = p.original_price_usd !== '' ? parseFloat(p.original_price_usd) : NaN;
                    const initVal = p.initial_price_usd !== '' ? parseFloat(p.initial_price_usd) : NaN;
                    return {
                        interval_key: p.interval_key,
                        price_usd: parseFloat(p.price_usd) || 0,
                        enabled: p.enabled,
                        original_price_usd: !isNaN(origVal) ? origVal : null,
                        initial_price_usd: !isNaN(initVal) ? initVal : null,
                        promo_code: p.promo_code || null,
                        show_discount_code: p.show_discount_code !== false,
                        trial_period_days: parseInt(p.trial_period_days) || 0,
                    };
                }),
            });
            if (res.data.warning) {
                toast.warn(res.data.warning);
            } else {
                toast.success('Subscription config saved!');
            }
            await loadConfig();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    const handleRemoveSubscriber = async (subId) => {
        if (!await confirm({ title: 'Remove subscriber?', message: 'Remove this subscriber from the channel?', confirmLabel: 'Remove', danger: true })) return;
        try {
            await api.post('/api/subscriptions/remove', { token, subscriber_id: subId });
            toast.success('Subscriber removed');
            loadSubscribers(subsPage);
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to remove');
        }
    };

    const updatePlan = (key, field, value) => {
        // Auto-expand Trial & Pricing when enabling a plan
        if (field === 'enabled' && value) {
            setExpandedPlans(prev => ({ ...prev, [key]: true }));
        }
        setPlans(prev => prev.map(p =>
            p.interval_key === key ? { ...p, [field]: value } : p
        ));
    };

    // Compute unsaved changes
    const isDirty = (() => {
        const s = savedConfigRef.current;
        if (!s) return false;
        if (s.enabled !== enabled) return true;
        if (s.promoText !== promoText) return true;
        if (s.ctaLabel !== ctaLabel) return true;
        if (s.promoBanner !== promoBanner) return true;
        if (s.vipChatTitle !== vipChatTitle) return true;
        for (const sp of s.plans) {
            const cp = plans.find(p => p.interval_key === sp.interval_key);
            if (!cp) return true;
            if (String(cp.price_usd) !== String(sp.price_usd)) return true;
            if (cp.enabled !== sp.enabled) return true;
            if (String(cp.original_price_usd || '') !== String(sp.original_price_usd || '')) return true;
            if (String(cp.initial_price_usd || '') !== String(sp.initial_price_usd || '')) return true;
            if (String(cp.promo_code || '') !== String(sp.promo_code || '')) return true;
            if (cp.show_discount_code !== sp.show_discount_code) return true;
            if ((cp.trial_period_days || 0) !== (sp.trial_period_days || 0)) return true;
        }
        return false;
    })();

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
                <div style={{ width: '32px', height: '32px', border: '3px solid var(--border)', borderTopColor: 'var(--accent)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            </div>
        );
    }

    return (
        <div style={{ fontFamily: fontStack }}>
            {/* How to set up */}
            <div style={{ marginBottom: '16px' }}>
                <button
                    onClick={() => setShowGuide(!showGuide)}
                    style={{
                        display: 'flex', alignItems: 'center', gap: '6px',
                        background: 'none', border: 'none', cursor: 'pointer',
                        fontSize: '13px', color: 'var(--accent)', fontWeight: 600, padding: 0,
                    }}
                >
                    <Icons.Book style={{ width: 14, height: 14 }} />
                    How to set up subscriptions
                    <svg width="10" height="6" viewBox="0 0 10 6" fill="none" style={{ transition: 'transform 0.2s', transform: showGuide ? 'rotate(180deg)' : 'none' }}>
                        <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </button>
                {showGuide && (
                    <div style={{
                        marginTop: '10px', padding: '16px 20px', borderRadius: '10px',
                        background: 'var(--bg-secondary, #f8fafc)', border: '1px solid var(--border)',
                        fontSize: '13px', lineHeight: 1.7, color: 'var(--text)',
                    }}>
                        <p style={{ margin: '0 0 10px', fontWeight: 600 }}>Quick Setup</p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            {[
                                { n: '1', text: 'Connect your VIP channel — a private Telegram group with "Request to Join" enabled' },
                                { n: '2', text: 'Toggle on the plans you want (Monthly and 1 Year are pre-configured)' },
                                { n: '3', text: 'Set your prices — minimum $3, strikethrough price to show a deal' },
                                { n: '4', text: 'Optionally add a first-period discount or free trial' },
                                { n: '5', text: 'Click Save Changes — payment plans are created automatically' },
                                { n: '6', text: 'Post a promo to your main channel to start getting subscribers' },
                            ].map(s => (
                                <div key={s.n} style={{ display: 'flex', gap: '10px' }}>
                                    <span style={{ color: 'var(--accent)', fontWeight: 700, minWidth: '18px' }}>{s.n}.</span>
                                    <span>{s.text}</span>
                                </div>
                            ))}
                        </div>
                        <p style={{ margin: '12px 0 0', fontSize: '12px', color: 'var(--text-muted)' }}>
                            For the full guide, click <strong>Guide</strong> in the sidebar and open "Setting Up Subscriptions".
                        </p>
                    </div>
                )}
            </div>

            {/* Analytics */}
            {analytics && (
                <div style={card}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700 }}>Analytics</h3>
                        <button onClick={loadAnalytics} disabled={analyticsLoading} style={btnGhost}>
                            {analyticsLoading ? 'Loading...' : 'Refresh'}
                        </button>
                    </div>

                    {/* Stat cards */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '20px' }}>
                        {[
                            { label: 'Checkouts', value: analytics.total_checkouts, bg: '#ecfdf5', border: '#bbf7d0', iconBg: '#16a34a', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg> },
                            { label: 'Total Subscribers', value: analytics.completed, bg: '#ecfdf5', border: '#bbf7d0', iconBg: '#16a34a', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/></svg> },
                            { label: 'MRR', value: `$${analytics.mrr.toFixed(2)}`, bg: '#ede9fe', border: '#c4b5fd', iconBg: '#7c3aed', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><circle cx="12" cy="12" r="3"/></svg> },
                            { label: 'Conversion Rate', value: `${analytics.conversion_rate}%`, bg: '#f3e8ff', border: '#d8b4fe', iconBg: '#8B5CF6', icon: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg> },
                        ].map((s, i) => (
                            <div key={i} style={{ padding: '16px', borderRadius: '12px', background: s.bg, border: `1px solid ${s.border}` }}>
                                <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: s.iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '8px' }}>{s.icon}</div>
                                <div style={{ fontSize: '12px', color: '#666', fontWeight: 500, marginBottom: '4px' }}>{s.label}</div>
                                <div style={{ fontSize: '24px', fontWeight: 700, color: '#1a1a1a' }}>{s.value}</div>
                            </div>
                        ))}
                    </div>

                    {/* Area chart */}
                    {analytics.daily_checkouts && (() => {
                        const data = analytics.daily_checkouts;
                        const W = 800, H = 200, padL = 40, padR = 10, padT = 10, padB = 28;
                        const cW = W - padL - padR, cH = H - padT - padB;
                        const rawMax = Math.max(...data.map(d => Math.max(d.checkouts, d.converted)));
                        const maxY = rawMax <= 2 ? 3 : rawMax <= 5 ? rawMax + 1 : rawMax <= 10 ? Math.ceil(rawMax * 1.2) : Math.ceil(rawMax * 1.2 / 5) * 5;
                        const xPos = (i) => padL + (i / (data.length - 1)) * cW;
                        const yPos = (v) => padT + cH - (v / maxY) * cH;
                        const smoothPath = (pts) => {
                            const n = pts.length;
                            if (n < 2) return `M${pts[0][0]},${pts[0][1]}`;
                            if (n === 2) return `M${pts[0][0]},${pts[0][1]} L${pts[1][0]},${pts[1][1]}`;
                            const dx = [], dy = [], m = [];
                            for (let i = 0; i < n - 1; i++) { dx.push(pts[i+1][0]-pts[i][0]); dy.push(pts[i+1][1]-pts[i][1]); m.push(dx[i]===0?0:dy[i]/dx[i]); }
                            const t = [m[0]];
                            for (let i = 1; i < n - 1; i++) { t.push(m[i-1]*m[i]<=0 ? 0 : 2/(1/m[i-1]+1/m[i])); }
                            t.push(m[n-2]);
                            let d = `M${pts[0][0].toFixed(1)},${pts[0][1].toFixed(1)}`;
                            for (let i = 0; i < n - 1; i++) { const s=dx[i]/3; d+=` C${(pts[i][0]+s).toFixed(1)},${(pts[i][1]+t[i]*s).toFixed(1)} ${(pts[i+1][0]-s).toFixed(1)},${(pts[i+1][1]-t[i+1]*s).toFixed(1)} ${pts[i+1][0].toFixed(1)},${pts[i+1][1].toFixed(1)}`; }
                            return d;
                        };
                        const checkoutPts = data.map((d, i) => [xPos(i), yPos(d.checkouts)]);
                        const convertedPts = data.map((d, i) => [xPos(i), yPos(d.converted)]);
                        const checkoutLine = smoothPath(checkoutPts);
                        const convertedLine = smoothPath(convertedPts);
                        const baseline = padT + cH;
                        const checkoutArea = `${checkoutLine} L${xPos(data.length-1)},${baseline} L${xPos(0)},${baseline} Z`;
                        const convertedArea = `${convertedLine} L${xPos(data.length-1)},${baseline} L${xPos(0)},${baseline} Z`;
                        const gridLines = [], gridLabels = [];
                        const gridCount = 4;
                        const gridStep = maxY / gridCount;
                        const niceStep = gridStep <= 1 ? 1 : gridStep <= 5 ? Math.ceil(gridStep) : gridStep <= 50 ? Math.ceil(gridStep / 10) * 10 : gridStep <= 500 ? Math.ceil(gridStep / 100) * 100 : Math.ceil(gridStep / 500) * 500;
                        for (let v = niceStep; v <= maxY; v += niceStep) { gridLines.push(yPos(v)); gridLabels.push(v >= 1000 ? (v/1000).toFixed(v%1000===0?0:1)+'k' : v); }
                        const labelIndices = Array.from({length: 5}, (_, i) => Math.round(i * (data.length-1) / 4));
                        const zeroDots = [];
                        data.forEach((d, i) => { if (i%7===0||i===data.length-1) { if(d.checkouts===0) zeroDots.push({x:xPos(i),color:'#8B5CF6'}); if(d.converted===0) zeroDots.push({x:xPos(i),color:'#10b981'}); }});
                        const hi = chartHover;
                        return (
                            <div style={{ marginBottom: '20px' }}>
                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '16px', marginBottom: '8px' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#666' }}><div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#8B5CF6' }} />Checkouts</div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#666' }}><div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#10b981' }} />Converted</div>
                                </div>
                                <div
                                    style={{ position: 'relative', cursor: 'crosshair' }}
                                    onMouseMove={(e) => {
                                        const svg = e.currentTarget.querySelector('svg');
                                        if (!svg) return;
                                        const rect = svg.getBoundingClientRect();
                                        const svgX = ((e.clientX - rect.left) / rect.width) * W;
                                        let closest = 0, minDist = Infinity;
                                        for (let i = 0; i < data.length; i++) { const dist = Math.abs(xPos(i) - svgX); if (dist < minDist) { minDist = dist; closest = i; } }
                                        setChartHover(closest);
                                    }}
                                    onMouseLeave={() => setChartHover(null)}
                                >
                                    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: '200px', display: 'block' }}>
                                        <defs>
                                            <linearGradient id="subChkGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#8B5CF6" stopOpacity="0.25" /><stop offset="100%" stopColor="#8B5CF6" stopOpacity="0.03" /></linearGradient>
                                            <linearGradient id="subConvGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10b981" stopOpacity="0.28" /><stop offset="100%" stopColor="#10b981" stopOpacity="0.03" /></linearGradient>
                                        </defs>
                                        {gridLines.map((gy, i) => (<g key={i}><line x1={padL} y1={gy} x2={W-padR} y2={gy} stroke="#e5e7eb" strokeWidth="1" strokeDasharray="4,4" /><text x={padL-6} y={gy+3} textAnchor="end" fontSize="9" fill="#bbb">{gridLabels[i]}</text></g>))}
                                        <line x1={xPos(data.length-1)} y1={padT} x2={xPos(data.length-1)} y2={baseline} stroke="#d1d5db" strokeWidth="1" strokeDasharray="3,3" />
                                        <text x={xPos(data.length-1)} y={padT-2} textAnchor="middle" fontSize="8" fill="#bbb">Today</text>
                                        <path d={checkoutArea} fill="url(#subChkGrad)" />
                                        <path d={checkoutLine} fill="none" stroke="#8B5CF6" strokeWidth="2" />
                                        <path d={convertedArea} fill="url(#subConvGrad)" />
                                        <path d={convertedLine} fill="none" stroke="#10b981" strokeWidth="2" />
                                        {zeroDots.map((dot, i) => (<circle key={`zd${i}`} cx={dot.x} cy={baseline} r="2.5" fill={dot.color} opacity="0.4" />))}
                                        {labelIndices.map(i => (<text key={`xl${i}`} x={xPos(i)} y={H-4} textAnchor="middle" fontSize="10" fill="#999">{data[i].date.slice(5)}</text>))}
                                        {hi !== null && (<g><line x1={xPos(hi)} y1={padT} x2={xPos(hi)} y2={baseline} stroke="#94a3b8" strokeWidth="1" strokeDasharray="3,3" /><circle cx={xPos(hi)} cy={checkoutPts[hi][1]} r="4" fill="#8B5CF6" stroke="#fff" strokeWidth="2" /><circle cx={xPos(hi)} cy={convertedPts[hi][1]} r="4" fill="#10b981" stroke="#fff" strokeWidth="2" /></g>)}
                                    </svg>
                                    {hi !== null && data[hi] && (
                                        <div style={{ position: 'absolute', left: `${(xPos(hi)/W)*100}%`, top: '0', transform: `translateX(${hi>data.length*0.7?'-110%':'10%'})`, background: '#1e293b', color: '#fff', borderRadius: '8px', padding: '8px 12px', fontSize: '12px', pointerEvents: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.2)', whiteSpace: 'nowrap', zIndex: 10 }}>
                                            <div style={{ fontWeight: 600, marginBottom: '4px' }}>{data[hi].date}</div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px' }}><div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#8B5CF6' }} />Checkouts: <strong>{data[hi].checkouts}</strong></div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}><div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#10b981' }} />Converted: <strong>{data[hi].converted}</strong></div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })()}

                    {/* Plan breakdown table */}
                    {analytics.plan_breakdown.length > 0 && (
                        <div>
                            <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '8px' }}>Plan Performance</div>
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                                    <thead><tr style={{ borderBottom: '1px solid var(--border)' }}>{['Plan', 'Active', ...(isDemoUser ? [] : ['Cancelled']), 'Abandoned', 'Conversion'].map(h => (<th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: 'var(--text-muted)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</th>))}</tr></thead>
                                    <tbody>{analytics.plan_breakdown.map((p, i) => { const total = p.active+p.churned+p.abandoned; const conv = total>0?((p.active+p.churned)/total*100).toFixed(0):'0'; const intv = INTERVALS.find(iv=>iv.key===p.interval_key); return (<tr key={i} style={{ borderBottom: '1px solid var(--border)' }}><td style={{ padding: '10px 12px', fontWeight: 600 }}>{intv?intv.label:p.interval_key}</td><td style={{ padding: '10px 12px', color: 'var(--success)' }}>{p.active}</td>{!isDemoUser && <td style={{ padding: '10px 12px', color: 'var(--error)' }}>{p.churned}</td>}<td style={{ padding: '10px 12px', color: 'var(--text-muted)' }}>{p.abandoned}</td><td style={{ padding: '10px 12px' }}>{conv}%</td></tr>); })}</tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Setup */}
            <div style={card}>
                <button
                    onClick={() => setChannelSetupOpen(!channelSetupOpen)}
                    style={{
                        display: 'flex', alignItems: 'center', width: '100%',
                        margin: 0, padding: 0, border: 'none', background: 'none', cursor: 'pointer',
                        fontSize: '16px', fontWeight: 600, color: 'var(--text)',
                    }}
                >
                    <Icons.Settings style={{ flexShrink: 0, width: 18, height: 18 }} />
                    <span style={{ marginLeft: '10px' }}>Channel Setup</span>
                    <span style={{
                        width: '8px', height: '8px', borderRadius: '50%', marginLeft: '10px', flexShrink: 0,
                        background: !vipChatId ? '#ccc' : !enabled ? '#f59e0b' : '#22c55e',
                    }} />
                    <span style={{ flex: 1 }} />
                    <svg width="10" height="6" viewBox="0 0 10 6" fill="none" style={{ transition: 'transform 0.2s', transform: channelSetupOpen ? 'rotate(180deg)' : 'none', flexShrink: 0 }}>
                        <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </button>
                {channelSetupOpen && (
                    <div style={{ marginTop: '16px' }}>
                        {vipChatId ? (
                            <div>
                                <span style={label}>Connected Channel</span>
                                <div style={{
                                    padding: '10px 14px', borderRadius: '8px',
                                    background: 'var(--success-bg)', border: '1px solid var(--success-border)',
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                }}>
                                    <div>
                                        <div style={{ fontWeight: 600, fontSize: '13px', color: 'var(--text)' }}>
                                            {vipChatTitle || 'Subscription Channel'}
                                        </div>
                                        <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
                                            ID: {vipChatId}
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', gap: '6px' }}>
                                        <button
                                            onClick={requestConnectVip}
                                            disabled={connectingVip}
                                            style={{ ...btnSmall, fontSize: '11px', padding: '5px 12px' }}
                                        >
                                            {connectingVip ? 'Waiting...' : 'Change'}
                                        </button>
                                        <button
                                            onClick={async () => {
                                                if (!await confirm('Disconnect this subscription channel? Existing subscribers will not be affected.')) return;
                                                setVipChatId('');
                                                setVipChatTitle('');
                                                setConfig(prev => prev ? { ...prev, vip_chat_id: null, vip_chat_title: '' } : prev);
                                                toast.success('Channel disconnected — save to apply');
                                            }}
                                            style={{ ...btnSmall, fontSize: '11px', padding: '5px 10px', color: 'var(--error)', background: 'var(--error-bg)', border: '1px solid var(--error-border)' }}
                                            title="Disconnect channel"
                                        >
                                            <Icons.Trash />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div>
                                <button
                                    onClick={requestConnectVip}
                                    disabled={connectingVip}
                                    style={{
                                        ...btnPrimary,
                                        width: 'auto', padding: '12px 28px', borderRadius: '10px',
                                        opacity: connectingVip ? 0.7 : 1,
                                    }}
                                >
                                    {connectingVip ? 'Waiting for selection...' : 'Connect Channel'}
                                </button>
                                {connectingVip && (
                                    <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: '8px 0 0' }}>
                                        Check your Telegram and select a group. This page will update automatically.
                                    </p>
                                )}
                            </div>
                        )}
                        <div style={{ marginTop: '14px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <button
                                onClick={() => setEnabled(!enabled)}
                                style={{
                                    width: '44px', height: '24px', borderRadius: '12px', border: 'none',
                                    background: enabled ? 'var(--accent)' : '#f59e0b',
                                    cursor: 'pointer', position: 'relative', transition: 'background 0.2s',
                                }}
                            >
                                <div style={{
                                    width: '18px', height: '18px', borderRadius: '50%', background: '#fff',
                                    position: 'absolute', top: '3px',
                                    left: enabled ? '23px' : '3px', transition: 'left 0.2s',
                                }} />
                            </button>
                            <div>
                                <span style={{ fontSize: '13px', color: enabled ? 'var(--text)' : '#b45309', fontWeight: enabled ? 400 : 600 }}>
                                    {enabled ? 'Accepting new subscribers' : 'New subscriptions paused'}
                                </span>
                                {!enabled && (
                                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
                                        Existing subscribers keep their access
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Promo Settings */}
            <div style={card}>
                <button
                    onClick={() => setPromoSettingsOpen(!promoSettingsOpen)}
                    style={{
                        display: 'flex', alignItems: 'center', width: '100%',
                        margin: 0, padding: 0, border: 'none', background: 'none', cursor: 'pointer',
                        fontSize: '16px', fontWeight: 600, color: 'var(--text)',
                    }}
                >
                    <Icons.Send style={{ flexShrink: 0, width: 18, height: 18 }} />
                    <span style={{ marginLeft: '10px' }}>Promo Settings</span>
                    <span style={{ flex: 1 }} />
                    <svg width="10" height="6" viewBox="0 0 10 6" fill="none" style={{ transition: 'transform 0.2s', transform: promoSettingsOpen ? 'rotate(180deg)' : 'none', flexShrink: 0 }}>
                        <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                </button>
                {promoSettingsOpen && (
                    <div style={{ marginTop: '16px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                            {/* Left: inputs */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                                <div>
                                    <span style={label}>Promo Banner</span>
                                    <input type="file" ref={bannerInputRef} onChange={handleBannerUpload} accept="image/jpeg,image/png,image/webp,image/gif" style={{ display: 'none' }} />
                                    {promoBanner ? (
                                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                                            <img src={`https://privly.co/api/dashboard/file?auth=${encodeURIComponent(localStorage.getItem('token')||'')}&file_id=${encodeURIComponent(promoBanner)}`} alt="Promo banner" style={{ width: '200px', height: 'auto', borderRadius: '8px', border: '1px solid var(--border)', objectFit: 'cover' }} />
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                <button onClick={() => bannerInputRef.current?.click()} disabled={uploadingBanner} style={{ ...btnSmall, fontSize: '11px', padding: '5px 12px' }}>{uploadingBanner ? 'Uploading...' : 'Change'}</button>
                                                <button onClick={() => setPromoBanner('')} style={{ ...btnSmall, fontSize: '11px', padding: '5px 12px', color: 'var(--error)' }}>Remove</button>
                                            </div>
                                        </div>
                                    ) : (
                                        <button onClick={() => bannerInputRef.current?.click()} disabled={uploadingBanner} style={{ ...btnSmall, padding: '10px 16px', fontSize: '12px', border: '2px dashed var(--border)', background: 'transparent', color: 'var(--text-muted)', borderRadius: '8px', opacity: uploadingBanner ? 0.7 : 1 }}>
                                            {uploadingBanner ? 'Uploading...' : 'Upload Banner Image'}
                                        </button>
                                    )}
                                </div>
                                <div>
                                    <span style={label}>Promo Text</span>
                                    <textarea style={{ ...dInput, minHeight: '80px', resize: 'vertical' }} placeholder="Get exclusive content..." value={promoText} onChange={e => setPromoText(e.target.value)} />
                                </div>
                                <div>
                                    <span style={label}>CTA Button Label</span>
                                    <input style={dInput} type="text" placeholder="Subscribe Now" value={ctaLabel} onChange={e => setCtaLabel(e.target.value)} />
                                </div>
                            </div>
                            {/* Right: Telegram preview */}
                            <div>
                                <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, marginBottom: '8px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Preview</label>
                                <div style={{ background: '#17212b', borderRadius: '12px', overflow: 'hidden', maxWidth: '360px', boxShadow: '0 2px 12px rgba(0,0,0,0.15)' }}>
                                    <div style={{ padding: '8px 12px 4px', fontSize: '13px', fontWeight: 700, color: '#6ab2f2' }}>{vipChatTitle || channel || 'Your Channel'}</div>
                                    {promoBanner ? (
                                        <img src={`https://privly.co/api/dashboard/file?auth=${encodeURIComponent(localStorage.getItem('token')||'')}&file_id=${encodeURIComponent(promoBanner)}`} alt="" style={{ width: '100%', maxHeight: '400px', objectFit: 'contain', display: 'block', background: '#0e1621' }} />
                                    ) : (
                                        <div style={{ width: '100%', height: '120px', background: 'linear-gradient(135deg, #1e2a3a 0%, #2a3a4a 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#4a5568" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
                                        </div>
                                    )}
                                    <div style={{ padding: '8px 12px', fontSize: '13px', color: '#e1e4e8', lineHeight: 1.4, whiteSpace: 'pre-wrap' }}>{promoText || 'Your promo text will appear here...'}</div>
                                    <div style={{ padding: '4px 8px 8px' }}>
                                        <div style={{ background: 'rgba(106,178,242,0.15)', borderRadius: '6px', padding: '8px', textAlign: 'center', fontSize: '13px', fontWeight: 500, color: '#6ab2f2', cursor: 'default' }}>{ctaLabel || 'Subscribe Now'}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* Post actions */}
                        <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid var(--border)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                                <button onClick={handlePostPromo} disabled={posting || !vipChatId} style={{ ...btnPrimary, width: 'auto', padding: '10px 24px', borderRadius: '10px', fontSize: '13px', opacity: (posting || !vipChatId) ? 0.5 : 1 }}>
                                    {posting ? 'Posting...' : 'Post Promo to Channel'}
                                </button>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <button onClick={() => setAutoPostEnabled(!autoPostEnabled)} style={{ width: '36px', height: '20px', borderRadius: '10px', border: 'none', background: autoPostEnabled ? '#8B5CF6' : 'var(--border)', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
                                        <div style={{ width: '14px', height: '14px', borderRadius: '50%', background: '#fff', position: 'absolute', top: '3px', left: autoPostEnabled ? '19px' : '3px', transition: 'left 0.2s' }} />
                                    </button>
                                    <span style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: 500 }}>Auto-post every</span>
                                    <input type="number" min="1" max="999" value={autoPostInterval} onChange={e => { const v = e.target.value; setAutoPostInterval(v === '' ? '' : Math.min(999, parseInt(v) || 0)); }} onBlur={() => { if (!autoPostInterval || autoPostInterval < 1) setAutoPostInterval(1); }} disabled={!autoPostEnabled} style={{ ...dInput, width: '50px', textAlign: 'center', fontSize: '12px', padding: '4px 6px', opacity: autoPostEnabled ? 1 : 0.4 }} />
                                    <div style={{ opacity: autoPostEnabled ? 1 : 0.4 }}>
                                        <CustomSelect value={autoPostUnit} onChange={v => setAutoPostUnit(v)} options={[{ value: 'hours', label: 'hours' }, { value: 'days', label: 'days' }]} style={{ fontSize: '12px', padding: '4px 8px', minWidth: '70px' }} />
                                    </div>
                                </div>
                            </div>
                            {autoPostEnabled && (
                                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '6px' }}>
                                    Promo will be automatically posted every {autoPostInterval} {autoPostUnit} to your channel.
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Plans */}
            <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: 'var(--text)' }}>
                        Subscription Plans
                    </h3>
                    <Icons.Settings style={{ width: 18, height: 18, color: 'var(--text-muted)' }} />
                </div>
                <div style={{ display: 'flex', gap: '10px', paddingBottom: '4px', alignItems: 'flex-start' }}>
                    {INTERVALS.map(interval => {
                        const plan = plans.find(p => p.interval_key === interval.key) || {};
                        const price = parseFloat(plan.price_usd) || 0;
                        const isOn = plan.enabled;
                        const periodSuffix = { weekly: '/wk', biweekly: '/2wk', monthly: '/mo', '3months': '/3mo', '1year': '/yr' }[interval.key] || '';
                        const activeCount = analytics?.plan_breakdown?.find(pb => pb.interval_key === interval.key)?.active || 0;
                        const periodName = interval.period || 'month';
                        return (
                            <div key={interval.key} style={{
                                flex: '1 1 0', minWidth: '150px',
                                borderRadius: '12px', padding: '16px',
                                background: isOn ? '#fff' : '#f5f5f7',
                                border: isOn ? '1px solid #e5e7eb' : '1px solid #ebebeb',
                                opacity: isOn ? 1 : 0.7,
                                display: 'flex', flexDirection: 'column',
                                overflow: 'visible', position: 'relative',
                            }}>
                                {/* Header: name + toggle */}
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                    <span style={{ fontSize: '13px', fontWeight: 600, color: isOn ? '#1a1a1a' : '#999' }}>{interval.label}</span>
                                    <button
                                        onClick={() => updatePlan(interval.key, 'enabled', !isOn)}
                                        style={{
                                            width: '48px', height: '22px', borderRadius: '11px', border: 'none',
                                            background: isOn ? '#8B5CF6' : '#ccc',
                                            cursor: 'pointer', position: 'relative',
                                            transition: 'background 0.2s',
                                            display: 'flex', alignItems: 'center',
                                            padding: '0 4px',
                                        }}
                                    >
                                        <span style={{
                                            fontSize: '9px', fontWeight: 700, color: '#fff',
                                            position: 'absolute',
                                            left: isOn ? '6px' : 'auto',
                                            right: isOn ? 'auto' : '6px',
                                            transition: 'left 0.2s, right 0.2s',
                                        }}>
                                            {isOn ? 'ON' : 'OFF'}
                                        </span>
                                        <div style={{
                                            width: '16px', height: '16px', borderRadius: '50%', background: '#fff',
                                            position: 'absolute', top: '3px',
                                            left: isOn ? '28px' : '4px',
                                            transition: 'left 0.2s',
                                            boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
                                        }} />
                                    </button>
                                </div>

                                {/* Price display / input */}
                                <div style={{ textAlign: 'center', marginBottom: '8px', minHeight: '40px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                    {isOn ? (
                                        <>
                                            {parseFloat(plan.original_price_usd) > price && price > 0 && (
                                                <span style={{ fontSize: '13px', color: '#bbb', textDecoration: 'line-through', lineHeight: 1 }}>
                                                    ${parseFloat(plan.original_price_usd).toFixed(2)}
                                                </span>
                                            )}
                                            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center' }}>
                                                <span style={{ fontSize: '11px', fontWeight: 600, color: '#999', marginRight: '1px' }}>$</span>
                                                <input
                                                    type="number" step="0.01" min="3" max="9999" placeholder="0"
                                                    value={plan.price_usd}
                                                    onChange={e => { const v = e.target.value; if (v === '' || parseFloat(v) <= 9999) updatePlan(interval.key, 'price_usd', v); }}
                                                    style={{
                                                        border: 'none', background: 'transparent', outline: 'none',
                                                        fontSize: '28px', fontWeight: 700, color: '#1a1a1a',
                                                        width: `${Math.max(2, Math.min(4, String(plan.price_usd || '0').length)) * 18}px`,
                                                        textAlign: 'right', padding: 0,
                                                        MozAppearance: 'textfield',
                                                    }}
                                                />
                                                <span style={{ fontSize: '14px', fontWeight: 400, color: '#999', marginLeft: '1px' }}>{periodSuffix}</span>
                                            </div>
                                        </>
                                    ) : (
                                        <div>
                                            <span style={{ fontSize: '28px', fontWeight: 700, color: '#ccc' }}>$0</span>
                                        </div>
                                    )}
                                </div>

                                {/* Active badge */}
                                <div style={{ textAlign: 'center', marginBottom: '14px' }}>
                                    <span style={{
                                        display: 'inline-block', padding: '3px 10px', borderRadius: '6px',
                                        fontSize: '11px', fontWeight: 600,
                                        background: isOn && activeCount > 0 ? 'rgba(34,197,94,0.1)' : '#f0f0f0',
                                        color: isOn && activeCount > 0 ? '#16a34a' : '#aaa',
                                    }}>
                                        {activeCount} active
                                    </span>
                                </div>

                                {/* Expandable rows */}
                                {['trial', 'firstperiod'].map((row, idx) => {
                                    const rowKey = `${interval.key}_${row}`;
                                    const isRowExpanded = expandedPlans[rowKey];
                                    const hasTrial = plan.trial_period_days > 0;
                                    const initPrice = parseFloat(plan.initial_price_usd);
                                    const hasDiscount = !isNaN(initPrice) && initPrice < price && price > 0;
                                    const rowLabel = row === 'trial' ? 'Trial days' : 'First-period discount';
                                    return (
                                        <div key={row}>
                                            {idx > 0 && <div style={{ height: '1px', background: '#eee', margin: '0' }} />}
                                            <button
                                                onClick={() => isOn && !(row === 'firstperiod' && hasTrial) && setExpandedPlans(prev => ({ ...prev, [rowKey]: !prev[rowKey] }))}
                                                style={{
                                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                                    width: '100%', padding: '10px 0', border: 'none', background: 'none',
                                                    cursor: isOn && !(row === 'firstperiod' && hasTrial) ? 'pointer' : 'default',
                                                    fontSize: '12px', color: isOn && !(row === 'firstperiod' && hasTrial) ? '#666' : '#bbb',
                                                    fontWeight: 500, pointerEvents: isOn ? 'auto' : 'none',
                                                }}
                                            >
                                                <span>
                                                    {rowLabel}
                                                    {row === 'firstperiod' && hasTrial && (
                                                        <span style={{ fontSize: '10px', color: '#bbb', marginLeft: '4px' }}>(disabled during trial)</span>
                                                    )}
                                                </span>
                                                <svg width="10" height="6" viewBox="0 0 10 6" fill="none" style={{ transition: 'transform 0.2s', transform: isRowExpanded ? 'rotate(180deg)' : 'none' }}>
                                                    <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                            </button>
                                            {isRowExpanded && (
                                                <div style={{ paddingBottom: '10px' }}>
                                                    {row === 'trial' && (
                                                        <CustomSelect
                                                            value={plan.trial_period_days || 0}
                                                            onChange={v => {
                                                                const days = parseInt(v);
                                                                updatePlan(interval.key, 'trial_period_days', days);
                                                                if (days > 0) {
                                                                    updatePlan(interval.key, 'initial_price_usd', '');
                                                                    updatePlan(interval.key, 'promo_code', '');
                                                                }
                                                            }}
                                                            options={[
                                                                { value: 0, label: 'None' },
                                                                { value: 1, label: '1 day' },
                                                                { value: 7, label: '7 days' },
                                                                { value: 30, label: '30 days' },
                                                            ]}
                                                            style={{ width: '100%', fontSize: '11px' }}
                                                        />
                                                    )}
                                                    {row === 'firstperiod' && (
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                            <div>
                                                                <span style={{ fontSize: '10px', color: '#999', marginBottom: '2px', display: 'block' }}>First {periodName} price</span>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                                    <span style={{ fontSize: '12px', color: '#999' }}>$</span>
                                                                    <input
                                                                        style={{ ...dInput, width: '100%', fontSize: '12px' }}
                                                                        type="number" step="0.01" min="0" placeholder={`e.g. ${Math.max(0, price * 0.5).toFixed(2)}`}
                                                                        value={plan.initial_price_usd}
                                                                        onChange={e => updatePlan(interval.key, 'initial_price_usd', e.target.value)}
                                                                    />
                                                                </div>
                                                            </div>
                                                            {hasDiscount && (
                                                                <div>
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                                                                        <button
                                                                            onClick={() => {
                                                                                if (plan.promo_code) {
                                                                                    updatePlan(interval.key, 'promo_code', '');
                                                                                } else {
                                                                                    updatePlan(interval.key, 'promo_code', 'SAVE' + Math.round((price - initPrice) / price * 100));
                                                                                }
                                                                            }}
                                                                            style={{
                                                                                width: '28px', height: '16px', borderRadius: '8px', border: 'none',
                                                                                background: plan.promo_code ? '#8B5CF6' : '#ccc',
                                                                                cursor: 'pointer', position: 'relative', flexShrink: 0,
                                                                            }}
                                                                        >
                                                                            <div style={{
                                                                                width: '10px', height: '10px', borderRadius: '50%', background: '#fff',
                                                                                position: 'absolute', top: '3px',
                                                                                left: plan.promo_code ? '15px' : '3px', transition: 'left 0.2s',
                                                                            }} />
                                                                        </button>
                                                                        <span style={{ fontSize: '10px', color: '#666' }}>Require promo code</span>
                                                                    </div>
                                                                    {plan.promo_code && (
                                                                        <div style={{ marginLeft: '34px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                                            <input
                                                                                style={{ ...dInput, width: '100%', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}
                                                                                type="text" placeholder="e.g. VIP50OFF"
                                                                                value={plan.promo_code}
                                                                                onChange={e => updatePlan(interval.key, 'promo_code', e.target.value.toUpperCase().replace(/[^A-Z0-9_-]/g, ''))}
                                                                            />
                                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                                <button
                                                                                    onClick={() => updatePlan(interval.key, 'show_discount_code', !plan.show_discount_code)}
                                                                                    style={{
                                                                                        width: '24px', height: '14px', borderRadius: '7px', border: 'none',
                                                                                        background: plan.show_discount_code ? '#8B5CF6' : '#ccc',
                                                                                        cursor: 'pointer', position: 'relative', flexShrink: 0,
                                                                                    }}
                                                                                >
                                                                                    <div style={{
                                                                                        width: '8px', height: '8px', borderRadius: '50%', background: '#fff',
                                                                                        position: 'absolute', top: '3px',
                                                                                        left: plan.show_discount_code ? '13px' : '3px', transition: 'left 0.2s',
                                                                                    }} />
                                                                                </button>
                                                                                <span style={{ fontSize: '9px', color: '#999' }}>Show on subscribe page</span>
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                            {/* Preview */}
                                                            {hasDiscount && (
                                                                <div style={{
                                                                    fontSize: '10px', color: '#16a34a', background: 'rgba(34,197,94,0.08)',
                                                                    padding: '6px 8px', borderRadius: '6px', marginTop: '2px',
                                                                }}>
                                                                    {plan.promo_code
                                                                        ? `Code ${plan.promo_code}: first ${periodName} $${initPrice.toFixed(2)} instead of $${price.toFixed(2)}`
                                                                        : `Everyone pays $${initPrice.toFixed(2)} for the first ${periodName} (instead of $${price.toFixed(2)})`
                                                                    }
                                                                </div>
                                                            )}
                                                            <div>
                                                                <span style={{ fontSize: '10px', color: '#999', marginBottom: '2px', display: 'block' }}>Strikethrough price (optional)</span>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                                    <span style={{ fontSize: '12px', color: '#999' }}>$</span>
                                                                    <input
                                                                        style={{ ...dInput, width: '100%', fontSize: '12px' }}
                                                                        type="number" step="0.01" min="0" placeholder="e.g. 29.99"
                                                                        value={plan.original_price_usd}
                                                                        onChange={e => updatePlan(interval.key, 'original_price_usd', e.target.value)}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
            </div>



            {/* Subscribers */}
            <div style={card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                    <div style={{ display: 'flex', gap: '0', borderRadius: '8px', overflow: 'hidden', border: '1px solid var(--border)' }}>
                        {[{ key: 'active', label: 'Active Subscribers' }, { key: 'expired', label: 'Expired Subscribers' }].map(tab => (
                            <button
                                key={tab.key}
                                onClick={() => switchSubsTab(tab.key)}
                                style={{
                                    padding: '8px 16px', fontSize: '13px', fontWeight: 600,
                                    border: 'none', cursor: 'pointer',
                                    background: subsTab === tab.key ? 'var(--accent)' : 'transparent',
                                    color: subsTab === tab.key ? '#fff' : 'var(--text-muted)',
                                    transition: 'all 0.15s',
                                }}
                            >
                                {tab.label} {subsTab === tab.key ? `(${subsTotal})` : ''}
                            </button>
                        ))}
                    </div>
                    <button onClick={() => loadSubscribers(subsPage)} style={btnGhost}>
                        Refresh
                    </button>
                </div>
                {subsLoading ? (
                    <div style={{ textAlign: 'center', padding: '30px' }}>
                        <div style={{ width: '24px', height: '24px', border: '2px solid var(--border)', borderTopColor: 'var(--accent)', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto' }} />
                    </div>
                ) : subscribers.length === 0 ? (
                    <p style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '30px 0', fontSize: '13px' }}>
                        {subsTab === 'active' ? 'No active subscribers' : 'No expired subscribers'}
                    </p>
                ) : (
                    <>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
                            {subscribers.map(sub => {
                                const sc = STATUS_COLORS[sub.status] || STATUS_COLORS.pending;
                                const intervalInfo = INTERVALS.find(i => i.key === sub.interval_key);
                                const isExpanded = expandedSub === sub.id;
                                const details = subDetails[sub.id];
                                const detailsLoading = subDetailsLoading[sub.id];
                                return (
                                    <div key={sub.id} style={{ borderBottom: '1px solid var(--border)' }}>
                                        <div
                                            onClick={() => toggleSubDetails(sub.id)}
                                            style={{
                                                display: 'flex', alignItems: 'center', gap: '12px',
                                                padding: '12px 10px', cursor: 'pointer',
                                                background: isExpanded ? 'var(--sidebar-item-active)' : 'transparent',
                                                transition: 'background 0.15s',
                                            }}
                                        >
                                            <span style={{ fontSize: '12px', color: 'var(--text-muted)', transform: isExpanded ? 'rotate(90deg)' : 'rotate(0)', transition: 'transform 0.2s' }}>&#9654;</span>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <span style={{ fontWeight: 500, fontSize: '13px', color: 'var(--text)' }}>
                                                    {sub.tg_username ? `@${sub.tg_username}` : sub.tg_user_id || 'Not connected'}
                                                </span>
                                                <span style={{ fontSize: '12px', color: 'var(--text-muted)', marginLeft: '8px' }}>
                                                    {intervalInfo ? intervalInfo.label : sub.interval_key}
                                                </span>
                                            </div>
                                            <span style={{
                                                display: 'inline-block', padding: '3px 10px',
                                                borderRadius: '20px', fontSize: '11px', fontWeight: 600,
                                                background: sc.bg, color: sc.color, border: `1px solid ${sc.border}`,
                                            }}>
                                                {sub.status === 'trialing' ? 'Trial' : sub.status}
                                            </span>
                                            {subsTab === 'active' && (
                                                <button
                                                    onClick={e => { e.stopPropagation(); handleRemoveSubscriber(sub.id); }}
                                                    style={{ ...btnDanger, padding: '4px 10px', fontSize: '11px', borderRadius: '6px' }}
                                                >
                                                    Remove
                                                </button>
                                            )}
                                        </div>
                                        {isExpanded && (
                                            <div style={{
                                                padding: '0 10px 14px 34px',
                                                background: 'var(--sidebar-item-active)',
                                            }}>
                                                {detailsLoading ? (
                                                    <div style={{ padding: '10px 0' }}>
                                                        <div style={{ width: '18px', height: '18px', border: '2px solid var(--border)', borderTopColor: 'var(--accent)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                                                    </div>
                                                ) : details ? (
                                                    <div style={{ fontSize: '12px', color: 'var(--text-muted)', lineHeight: '1.8' }}>
                                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 24px', maxWidth: '500px' }}>
                                                            <span style={{ color: 'var(--text)', fontWeight: 600 }}>Subscribed</span>
                                                            <span>{sub.created_at ? new Date(sub.created_at).toLocaleDateString() : '-'}</span>

                                                            {sub.status === 'trialing' && (
                                                                <>
                                                                    <span style={{ color: 'var(--text)', fontWeight: 600 }}>Trial</span>
                                                                    <span style={{ color: '#818cf8' }}>1-day free trial</span>
                                                                </>
                                                            )}

                                                            <span style={{ color: 'var(--text)', fontWeight: 600 }}>Last payment</span>
                                                            <span>{details.subscriber?.last_payment_at ? new Date(details.subscriber.last_payment_at).toLocaleDateString() : '-'}</span>

                                                            {details.membership?.renewal_period_end && (
                                                                <>
                                                                    <span style={{ color: 'var(--text)', fontWeight: 600 }}>Next billing</span>
                                                                    <span>{new Date(details.membership.renewal_period_end).toLocaleDateString()}</span>
                                                                </>
                                                            )}

                                                            {details.membership?.cancel_at_period_end && (
                                                                <>
                                                                    <span style={{ color: 'var(--text)', fontWeight: 600 }}>Cancels at</span>
                                                                    <span style={{ color: 'var(--error)' }}>End of period</span>
                                                                </>
                                                            )}


                                                            {details.membership?.user?.name && (
                                                                <>
                                                                    <span style={{ color: 'var(--text)', fontWeight: 600 }}>Name</span>
                                                                    <span>{details.membership.user.name}</span>
                                                                </>
                                                            )}

                                                            {details.membership?.status && (
                                                                <>
                                                                    <span style={{ color: 'var(--text)', fontWeight: 600 }}>Membership status</span>
                                                                    <span>{details.membership.status}</span>
                                                                </>
                                                            )}
                                                        </div>

                                                        {details.payments && details.payments.length > 0 && (
                                                            <div style={{ marginTop: '12px' }}>
                                                                <span style={{ color: 'var(--text)', fontWeight: 600, fontSize: '12px' }}>Payment history</span>
                                                                <div style={{ marginTop: '6px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                                    {details.payments.map((p, i) => (
                                                                        <div key={p.id || i} style={{
                                                                            display: 'flex', alignItems: 'center', gap: '10px',
                                                                            padding: '6px 10px', borderRadius: '6px',
                                                                            background: 'var(--bg)', border: '1px solid var(--border)',
                                                                        }}>
                                                                            <span style={{ fontWeight: 600, color: p.status === 'paid' ? 'var(--success)' : 'var(--error)' }}>
                                                                                ${typeof p.amount === 'number' ? p.amount.toFixed(2) : p.amount || '?'}
                                                                            </span>
                                                                            <span style={{ flex: 1 }}>
                                                                                {p.paid_at ? new Date(p.paid_at).toLocaleDateString() : p.created_at ? new Date(p.created_at).toLocaleDateString() : '-'}
                                                                            </span>
                                                                            <span style={{
                                                                                padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 600,
                                                                                background: p.status === 'paid' ? 'var(--success-bg)' : 'var(--error-bg)',
                                                                                color: p.status === 'paid' ? 'var(--success)' : 'var(--error)',
                                                                            }}>
                                                                                {p.status}
                                                                            </span>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}

                                                    </div>
                                                ) : (
                                                    <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                                                        No details available
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        {subsTotal > 20 && (
                            <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '16px' }}>
                                <button
                                    onClick={() => loadSubscribers(subsPage - 1)}
                                    disabled={subsPage <= 1}
                                    style={{ ...btnSmall, opacity: subsPage <= 1 ? 0.4 : 1 }}
                                >
                                    Prev
                                </button>
                                <span style={{ fontSize: '13px', color: 'var(--text-muted)', lineHeight: '32px' }}>
                                    Page {subsPage} of {Math.ceil(subsTotal / 20)}
                                </span>
                                <button
                                    onClick={() => loadSubscribers(subsPage + 1)}
                                    disabled={subsPage >= Math.ceil(subsTotal / 20)}
                                    style={{ ...btnSmall, opacity: subsPage >= Math.ceil(subsTotal / 20) ? 0.4 : 1 }}
                                >
                                    Next
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
            <ConfirmModal {...confirmProps} />

            {/* Sticky save bar */}
            {isDirty && (
                <div style={{ position: 'fixed', bottom: '24px', left: 0, right: 0, zIndex: 100, display: 'flex', justifyContent: 'center', animation: 'slideUpSave 0.25s ease', pointerEvents: 'none' }}>
                    <div style={{
                        background: '#fff',
                        padding: '12px 20px',
                        display: 'flex', alignItems: 'center', gap: '12px',
                        borderRadius: '12px',
                        border: '1.5px solid #10b981',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                        pointerEvents: 'auto',
                    }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" fill="rgba(16,185,129,0.1)" stroke="#10b981"/><path d="M9 12l2 2 4-4"/></svg>
                        <span style={{ fontSize: '14px', fontWeight: 500, color: '#1a1a1a' }}>Unsaved changes</span>
                        <button
                            onClick={handleSave}
                            disabled={saving}
                            style={{
                                padding: '6px 18px', borderRadius: '8px', border: 'none',
                                background: '#10b981', color: '#fff', fontSize: '13px', fontWeight: 600,
                                cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1,
                                marginLeft: '4px',
                            }}
                        >
                            {saving ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </div>
            )}
            <style>{`@keyframes slideUpSave { from { transform: translateY(100%); } to { transform: translateY(0); } }`}</style>
        </div>
    );
}
